import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { MultiSelect } from "../ui/multi-select";
import { toast } from "sonner";
import { fetchUsers } from "../../services/crmServices/userService.js";
import { createDesk, updateDesk } from "../../services/crmServices/deskService.js";
import { Select } from "../ui/select";
import { getFullName, updateFirstLetterToUpperCase } from "../../services/common/util.js";
import { useAuth } from "../../hooks/useAuth";
import { floors } from "../../services/common/constants.js";
import { set } from "date-fns";

export function CreateDeskModal({ open, onClose, onSuccess, desk }) {
    const { user } = useAuth();
    const [formData, setFormData] = useState({
        name: "",
        agents: [],
        manager: null,
        floorManager: null,
        floor: ""
    });

    const [allAgents, setAllAgents] = useState([]);
    const [availableAgents, setAvailableAgents] = useState([]);
    const [allManagers, setAllManagers] = useState([]);
    const [availableManagers, setAvailableManagers] = useState([]);
    const [allFloorManagers, setAllFloorManagers] = useState([]);
    const [availableFloorManagers, setAvailableFloorManagers] = useState([]);

    useEffect(() => {
        if (open) {
            if (desk) {
                setFormData({
                    name: desk.name || "",
                    agents: desk.agents.map((item) => item._id) || [],
                    manager: desk.manager?._id,
                    floorManager: desk.floorManager?._id,
                    floor: desk.floor || ""
                });
            }

            fetchUsers().then((res) => {
                if (res?.data?.length) {
                    if (desk) {
                        const agentsTemp = res.data.filter(
                            (user) =>
                                user.position.role === "agent" &&
                                user.position.floor === desk.floor &&
                                (!user.agentDesk || user.agentDesk?._id == desk._id)
                        );
                        setAvailableAgents(agentsTemp);
                        const allAgentsTemp = res.data.filter(
                            (user) =>
                                user.position.role === "agent" && (!user.agentDesk || user.agentDesk?._id == desk._id)
                        );
                        setAllAgents(allAgentsTemp);
                        setAvailableManagers(
                            res.data.filter(
                                (user) => user.position.role === "deskManager" && user.position.floor === desk.floor
                            )
                        );
                        setAvailableFloorManagers(
                            res.data.filter(
                                (user) => user.position.role === "floorManager" && user.position.floor === desk.floor
                            )
                        );
                    } else {
                        const allAgentsTemp = res.data.filter(
                            (user) => user.position.role === "agent" && !user.agentDesk
                        );
                        setAllAgents(allAgentsTemp);
                    }

                    setAllManagers(res.data.filter((user) => user.position.role === "deskManager"));
                    setAllFloorManagers(res.data.filter((user) => user.position.role === "floorManager"));
                }
            });
        }
    }, [open, desk]);

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.name) {
            toast.error("Please enter a desk name");
            return;
        }

        if (!formData.floor) {
            toast.error("Please select a floor");
            return;
        }

        if (desk) {
            updateDesk({ ...formData, _id: desk._id }).then((response) => {
                if (response?.status === 200) {
                    toast.success("Desk updated successfully!");
                    setFormData({ name: "", agents: [], manager: null, floorManager: null, floor: "" });
                    onSuccess();
                    onClose();
                } else {
                    if (response.data?.RESULT === "ERROR") {
                        toast.error(response.data.message);
                    } else {
                        toast.error(response.message || "Failed to update desk");
                    }
                }
            });
        } else {
            createDesk(formData).then((response) => {
                if (response?.status === 200) {
                    toast.success("Desk created successfully!");
                    setFormData({ name: "", agents: [], manager: null, floorManager: null, floor: "" });
                    onSuccess();
                    onClose();
                } else {
                    if (response.data?.RESULT === "ERROR") {
                        toast.error(response.data.message);
                    } else {
                        toast.error(response.message || "Failed to create desk");
                    }
                }
            });
        }
    };

    const handleFloorManagerChange = (value) => {
        if (value !== formData.floorManager) {
            setFormData((prev) => ({ ...prev, floorManager: value }));
        }
    };

    const handleManagerChange = (value) => {
        if (value !== formData.manager) {
            setFormData((prev) => ({ ...prev, manager: value }));
        }
    };

    const handleFloorChange = (value) => {
        if (value !== formData.floor) {
            const filteredManagers = allManagers.filter((manager) => manager.position.floor === value);
            const filteredFloorManagers = allFloorManagers.filter(
                (floorManager) => floorManager.position.floor === value
            );
            const filteredAgents = allAgents.filter((agent) => agent.position.floor === value);
            setAvailableManagers(filteredManagers);
            setAvailableFloorManagers(filteredFloorManagers);
            setAvailableAgents(filteredAgents);
            setFormData((prev) => ({ ...prev, floor: value, manager: null, floorManager: null, agents: [] }));
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>{desk ? "Edit" : "Create New"} Desk</DialogTitle>
                    {!desk && <DialogDescription>Set up a new desk and assign team assignedAgents.</DialogDescription>}
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <Label htmlFor="name">Desk Name *</Label>
                        <Input
                            id="name"
                            value={formData.name}
                            onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                            placeholder="e.g., Sales Desk A"
                            required
                        />
                    </div>

                    <div>
                        <Label htmlFor="floor">Floor *</Label>
                        <Select
                            options={floors.map((floor) => ({
                                label: floor.label,
                                value: floor.value
                            }))}
                            value={formData.floor}
                            onChange={(value) => handleFloorChange(value)}
                            placeholder="Select Floor"
                            searchable
                            disabled={user.position.role !== "admin"}
                        />
                    </div>
                    <div className="mb-4">
                        <Label htmlFor="floorManager">Assign Floor Manager</Label>
                        <Select
                            options={availableFloorManagers.map((floorManager) => ({
                                label: `${getFullName(floorManager)} (${
                                    floorManager.position.floor
                                        ? updateFirstLetterToUpperCase(floorManager.position.floor) + " Floor Manager"
                                        : "N/A"
                                })`,
                                value: floorManager._id
                            }))}
                            value={formData.floorManager || null}
                            onChange={(value) => handleFloorManagerChange(value)}
                            placeholder="Select Floor Manager"
                            searchable
                            disabled={user.position.role !== "admin" || !formData.floor}
                        />
                    </div>
                    <div className="mb-4">
                        <Label htmlFor="desk">Assign Manager</Label>
                        <Select
                            options={availableManagers.map((manager) => ({
                                label: `${getFullName(manager)} (${
                                    manager.position.floor
                                        ? updateFirstLetterToUpperCase(manager.position.floor) + " Manager"
                                        : "N/A"
                                })`,
                                value: manager._id
                            }))}
                            value={formData.manager || null}
                            onChange={(value) => handleManagerChange(value)}
                            placeholder="Select Manager"
                            searchable
                            disabled={!/admin|floorManager/.test(user.position.role) || !formData.floor}
                        />
                    </div>
                    <div>
                        <Label>Assign Agents</Label>
                        <MultiSelect
                            key={open ? "open" : "closed"}
                            options={availableAgents.map((agent) => ({
                                label: `${getFullName(agent)} (${
                                    agent.position.floor ? updateFirstLetterToUpperCase(agent.position.floor) : "N/A"
                                })`,
                                value: agent._id
                            }))}
                            selected={formData.agents}
                            onChange={(selected) => setFormData((prev) => ({ ...prev, agents: selected }))}
                            placeholder="Select agents"
                            searchPlaceholder="Search agents..."
                            disabled={!formData.manager}
                        />
                        <p className="text-sm text-gray-500 mt-2">Selected: {formData.agents.length} agents</p>
                    </div>

                    <div className="flex space-x-4 pt-4">
                        <Button type="submit" className="">
                            Save Changes
                        </Button>
                        <Button type="button" variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
